import React, { useState, useEffect } from 'react';
import { 
  getCustomAdminRings, 
  addCustomAdminRing, 
  grantAdminRing, 
  ALL_RINGS, 
  RING_CATEGORIES 
} from '../services/profileRingService';
import { db, storage } from '../services/firebase';
import { collection, query, where, getDocs } from 'firebase/firestore';
import { ref, uploadBytes, getDownloadURL } from 'firebase/storage';
import ProfileRing from './ProfileRing';

function AdminRingManager({ isOpen, onClose, adminUserId }) {
  const [customRings, setCustomRings] = useState([]);
  const [loading, setLoading] = useState(true);
  const [activeTab, setActiveTab] = useState('manage'); // 'manage' or 'create'
  const [users, setUsers] = useState([]);
  const [selectedUser, setSelectedUser] = useState('');
  const [selectedRing, setSelectedRing] = useState('');
  const [granting, setGranting] = useState(false);
  
  // New ring creation form
  const [newRing, setNewRing] = useState({
    name: '',
    description: '',
    cssClass: '',
    imageUrl: ''
  });
  const [creating, setCreating] = useState(false);
  const [selectedFile, setSelectedFile] = useState(null);
  const [uploadingImage, setUploadingImage] = useState(false);
  const [imagePreview, setImagePreview] = useState('');
  const [showImageEditor, setShowImageEditor] = useState(false);
  const [imageEditor, setImageEditor] = useState({
    scale: 1,
    rotate: 0,
    x: 0,
    y: 0,
    borderWidth: 8,
    originalImage: null
  });

  useEffect(() => {
    if (!isOpen) return;

    const loadData = async () => {
      setLoading(true);
      try {
        // Load custom admin rings
        const rings = await getCustomAdminRings();
        setCustomRings(rings);
        
        // Load users for granting rings
        const usersQuery = query(collection(db, 'users'));
        const usersSnapshot = await getDocs(usersQuery);
        const usersData = usersSnapshot.docs.map(doc => ({
          id: doc.id,
          ...doc.data()
        }));
        setUsers(usersData);
      } catch (error) {
        console.error('Error loading admin ring data:', error);
      } finally {
        setLoading(false);
      }
    };

    loadData();
  }, [isOpen]);

  const handleFileSelect = (e) => {
    const file = e.target.files[0];
    if (!file) return;

    // Validate file type
    if (!file.type.startsWith('image/')) {
      alert('Please select an image file');
      return;
    }

    // Validate file size (max 5MB)
    if (file.size > 5 * 1024 * 1024) {
      alert('Image size must be less than 5MB');
      return;
    }

    setSelectedFile(file);
    
    // Create preview and open editor
    const reader = new FileReader();
    reader.onload = (e) => {
      const imageUrl = e.target.result;
      setImagePreview(imageUrl);
      setImageEditor(prev => ({
        ...prev,
        originalImage: imageUrl,
        scale: 1,
        rotate: 0,
        x: 0,
        y: 0
      }));
      setShowImageEditor(true);
    };
    reader.readAsDataURL(file);
  };

  const uploadImage = async () => {
    if (!selectedFile) return null;

    setUploadingImage(true);
    try {
      // Create unique filename
      const fileName = `ring_images/${adminUserId}_${Date.now()}_${selectedFile.name}`;
      const storageRef = ref(storage, fileName);
      
      // Upload file
      const uploadResult = await uploadBytes(storageRef, selectedFile);
      
      // Get download URL
      const downloadURL = await getDownloadURL(uploadResult.ref);
      
      return downloadURL;
    } catch (error) {
      console.error('Error uploading image:', error);
      throw new Error('Failed to upload image');
    } finally {
      setUploadingImage(false);
    }
  };

  const clearImageSelection = () => {
    setSelectedFile(null);
    setImagePreview('');
    setShowImageEditor(false);
    setNewRing({...newRing, imageUrl: ''});
    setImageEditor({
      scale: 1,
      rotate: 0,
      x: 0,
      y: 0,
      borderWidth: 8,
      originalImage: null
    });
  };

  const applyImageEdits = () => {
    if (!imageEditor.originalImage) return;
    
    // Create canvas to apply edits
    const canvas = document.createElement('canvas');
    const ctx = canvas.getContext('2d');
    const img = new Image();
    
    img.onload = () => {
      const size = 400; // Larger ring image size for more space
      canvas.width = size;
      canvas.height = size;
      
      // Clear canvas with transparent background
      ctx.clearRect(0, 0, size, size);
      
      // Apply transformations
      ctx.save();
      ctx.translate(size / 2, size / 2);
      ctx.rotate((imageEditor.rotate * Math.PI) / 180);
      ctx.scale(imageEditor.scale, imageEditor.scale);
      
      // Draw image with much larger area for flames/effects
      const drawSize = size * 2; // Much larger drawing area
      ctx.drawImage(
        img,
        -drawSize / 2 + imageEditor.x,
        -drawSize / 2 + imageEditor.y,
        drawSize,
        drawSize
      );
      
      ctx.restore();
      
      // Convert to blob and update preview
      canvas.toBlob((blob) => {
        const reader = new FileReader();
        reader.onload = (e) => {
          setImagePreview(e.target.result);
          // Update the selected file with the edited version
          const editedFile = new File([blob], selectedFile.name, { type: selectedFile.type });
          setSelectedFile(editedFile);
          setShowImageEditor(false);
        };
        reader.readAsDataURL(blob);
      }, selectedFile.type, 0.95); // Higher quality for flames
    };
    
    img.src = imageEditor.originalImage;
  };

  const handleCreateRing = async (e) => {
    e.preventDefault();
    
    if (!newRing.name.trim() || !newRing.description.trim()) {
      alert('Please fill in name and description');
      return;
    }
    
    setCreating(true);
    try {
      let imageUrl = newRing.imageUrl.trim();
      
      // Upload image if file is selected
      if (selectedFile) {
        imageUrl = await uploadImage();
      }
      
      const ringData = {
        name: newRing.name.trim(),
        description: newRing.description.trim(),
        cssClass: newRing.cssClass.trim() || `ring-custom-${Date.now()}`,
        imageUrl: imageUrl
      };
      
      const ringId = await addCustomAdminRing(ringData, adminUserId);
      
      if (ringId) {
        // Add to local state
        setCustomRings(prev => [...prev, { id: ringId, ...ringData }]);
        
        // Reset form
        setNewRing({
          name: '',
          description: '',
          cssClass: '',
          imageUrl: ''
        });
        clearImageSelection();
        
        alert('Custom ring created successfully!');
        setActiveTab('manage');
      } else {
        alert('Failed to create ring: Database operation failed. Check console for details.');
      }
    } catch (error) {
      console.error('Error creating ring:', error);
      alert(`Failed to create ring: ${error.message || 'Unknown error'}`);
    } finally {
      setCreating(false);
    }
  };

  const handleGrantRing = async (e) => {
    e.preventDefault();
    
    if (!selectedUser || !selectedRing) {
      alert('Please select both user and ring');
      return;
    }
    
    setGranting(true);
    try {
      const result = await grantAdminRing(selectedUser, selectedRing, adminUserId);
      
      if (result.success) {
        alert(result.message || 'Ring granted successfully!');
        setSelectedUser('');
        setSelectedRing('');
      } else {
        // Provide specific error messages
        let errorMessage;
        switch (result.reason) {
          case 'already_owned':
            errorMessage = 'User already owns this ring';
            break;
          case 'ring_not_found':
            errorMessage = 'Ring not found in system';
            break;
          case 'invalid_category':
            errorMessage = 'Ring is not an admin special ring';
            break;
          default:
            errorMessage = result.message || 'Failed to grant ring';
        }
        alert(errorMessage);
      }
    } catch (error) {
      console.error('Error granting ring:', error);
      alert('Failed to grant ring: ' + error.message);
    } finally {
      setGranting(false);
    }
  };

  const getAvailableAdminRings = () => {
    const defaultAdminRings = Object.entries(ALL_RINGS)
      .filter(([_, ring]) => ring.category === RING_CATEGORIES.ADMIN_SPECIAL)
      .map(([id, ring]) => ({ id, ...ring }));
    
    return [...defaultAdminRings, ...customRings];
  };

  if (!isOpen) return null;

  return (
    <div className="admin-ring-modal-overlay" onClick={onClose}>
      <div className="admin-ring-modal-container" onClick={(e) => e.stopPropagation()}>
        <div className="admin-ring-modal-header">
          <h2>Admin Ring Manager</h2>
          <button className="admin-ring-modal-close" onClick={onClose}>×</button>
        </div>

        {/* Tab Navigation */}
        <div className="admin-ring-tabs">
          <button 
            className={`tab-button ${activeTab === 'manage' ? 'active' : ''}`}
            onClick={() => setActiveTab('manage')}
          >
            Grant Rings
          </button>
          <button 
            className={`tab-button ${activeTab === 'create' ? 'active' : ''}`}
            onClick={() => setActiveTab('create')}
          >
            Create Custom Ring
          </button>
        </div>

        {loading ? (
          <div className="admin-ring-loading">
            <div className="emoji-loader">
              <span className="emoji-item">👑</span>
              <span className="emoji-item">💍</span>
              <span className="emoji-item">⚡</span>
            </div>
            <p>Loading admin tools...</p>
          </div>
        ) : (
          <>
            {/* Grant Rings Tab */}
            {activeTab === 'manage' && (
              <div className="grant-rings-section">
                <h3>Grant Special Rings to Users</h3>
                
                <form onSubmit={handleGrantRing} className="grant-ring-form">
                  <div className="form-group">
                    <label>Select User</label>
                    <select 
                      value={selectedUser} 
                      onChange={(e) => setSelectedUser(e.target.value)}
                      required
                    >
                      <option value="">Choose a user...</option>
                      {users.map(user => (
                        <option key={user.id} value={user.id}>
                          {user.displayName || user.username || user.email || user.id}
                        </option>
                      ))}
                    </select>
                  </div>
                  
                  <div className="form-group">
                    <label>Select Ring</label>
                    <select 
                      value={selectedRing} 
                      onChange={(e) => setSelectedRing(e.target.value)}
                      required
                    >
                      <option value="">Choose a ring...</option>
                      {getAvailableAdminRings().map(ring => (
                        <option key={ring.id} value={ring.id}>
                          {ring.name} - {ring.description}
                        </option>
                      ))}
                    </select>
                  </div>
                  
                  {selectedRing && (
                    <div className="ring-preview">
                      <h4>Preview</h4>
                      <ProfileRing ringId={selectedRing} size="medium">
                        <div className="demo-avatar-small">
                          <span>👤</span>
                        </div>
                      </ProfileRing>
                    </div>
                  )}
                  
                  <button 
                    type="submit" 
                    className="grant-ring-button"
                    disabled={granting}
                  >
                    {granting ? 'Granting...' : 'Grant Ring'}
                  </button>
                </form>
                
                {/* Available Admin Rings */}
                <div className="admin-rings-list">
                  <h4>Available Admin Rings</h4>
                  <div className="admin-rings-grid">
                    {getAvailableAdminRings().map(ring => (
                      <div key={ring.id} className="admin-ring-item">
                        <ProfileRing ringId={ring.id} size="small">
                          <div className="mini-avatar">
                            <span>👤</span>
                          </div>
                        </ProfileRing>
                        <div className="admin-ring-info">
                          <div className="admin-ring-name">{ring.name}</div>
                          <div className="admin-ring-description">{ring.description}</div>
                        </div>
                      </div>
                    ))}
                  </div>
                </div>
              </div>
            )}

            {/* Create Custom Ring Tab */}
            {activeTab === 'create' && (
              <div className="create-ring-section">
                <h3>Create Custom Admin Ring</h3>
                
                <form onSubmit={handleCreateRing} className="create-ring-form">
                  <div className="form-group">
                    <label>Ring Name</label>
                    <input
                      type="text"
                      value={newRing.name}
                      onChange={(e) => setNewRing({...newRing, name: e.target.value})}
                      placeholder="Enter ring name"
                      maxLength={50}
                      required
                    />
                  </div>
                  
                  <div className="form-group">
                    <label>Description</label>
                    <textarea
                      value={newRing.description}
                      onChange={(e) => setNewRing({...newRing, description: e.target.value})}
                      placeholder="Describe this ring"
                      maxLength={200}
                      rows={3}
                      required
                    />
                  </div>
                  
                  <div className="form-group">
                    <label>CSS Class (Optional)</label>
                    <input
                      type="text"
                      value={newRing.cssClass}
                      onChange={(e) => setNewRing({...newRing, cssClass: e.target.value})}
                      placeholder="custom-ring-class"
                      maxLength={50}
                    />
                    <div className="form-help">
                      Leave empty to auto-generate. Use for custom styling.
                    </div>
                  </div>
                  
                  <div className="form-group">
                    <label>Ring Image</label>
                    
                    {/* File Upload Section */}
                    <div className="image-upload-section">
                      <div className="upload-options">
                        <div className="upload-option">
                          <label className="file-upload-label">
                            <input
                              type="file"
                              accept="image/*"
                              onChange={handleFileSelect}
                              style={{ display: 'none' }}
                            />
                            📁 Upload Image File
                          </label>
                          <div className="form-help">
                            Upload PNG, JPG, or GIF (max 5MB)
                          </div>
                        </div>
                        
                        <div className="upload-divider">OR</div>
                        
                        <div className="upload-option">
                          <input
                            type="url"
                            value={newRing.imageUrl}
                            onChange={(e) => setNewRing({...newRing, imageUrl: e.target.value})}
                            placeholder="🔗 https://example.com/ring-image.png"
                          />
                          <div className="form-help">
                            Enter a direct image URL
                          </div>
                        </div>
                      </div>

                      {/* Image Preview */}
                      {(imagePreview || newRing.imageUrl) && (
                        <div className="image-preview-section">
                          <label>Preview</label>
                          <div className="image-preview-container">
                            <img 
                              src={imagePreview || newRing.imageUrl} 
                              alt="Ring preview"
                              className="ring-image-preview"
                              onError={(e) => {
                                e.target.style.display = 'none';
                                e.target.nextSibling.style.display = 'block';
                              }}
                            />
                            <div className="image-error" style={{ display: 'none' }}>
                              ❌ Failed to load image
                            </div>
                            <button 
                              type="button" 
                              className="clear-image-button"
                              onClick={clearImageSelection}
                            >
                              ✕ Clear
                            </button>
                          </div>
                        </div>
                      )}

                      {selectedFile && (
                        <div className="file-info">
                          <span className="file-name">📄 {selectedFile.name}</span>
                          <span className="file-size">({(selectedFile.size / 1024 / 1024).toFixed(2)} MB)</span>
                        </div>
                      )}
                    </div>
                  </div>

                  {/* Image Editor Modal */}
                  {showImageEditor && (
                    <div className="image-editor-overlay" onClick={() => setShowImageEditor(false)}>
                      <div className="image-editor-modal" onClick={(e) => e.stopPropagation()}>
                        <div className="image-editor-header">
                          <h3>Edit Ring Image</h3>
                          <button 
                            className="editor-close-button"
                            onClick={() => setShowImageEditor(false)}
                          >
                            ✕
                          </button>
                        </div>
                        
                        <div className="image-editor-content">
                          <div className="image-editor-preview">
                            <div className="ring-preview-container">
                              <div 
                                className="editable-ring-image"
                                style={{
                                  backgroundImage: `url(${imageEditor.originalImage})`,
                                  transform: `scale(${imageEditor.scale}) rotate(${imageEditor.rotate}deg) translate(${imageEditor.x}px, ${imageEditor.y}px)`,
                                  borderWidth: `${imageEditor.borderWidth}px`
                                }}
                              />
                              <div className="ring-mask-overlay" />
                            </div>
                          </div>
                          
                          <div className="image-editor-controls">
                            <div className="control-group">
                              <label>Scale</label>
                              <input
                                type="range"
                                min="0.1"
                                max="5"
                                step="0.1"
                                value={imageEditor.scale}
                                onChange={(e) => setImageEditor(prev => ({...prev, scale: parseFloat(e.target.value)}))}
                              />
                              <span>{imageEditor.scale.toFixed(1)}x</span>
                            </div>
                            
                            <div className="control-group">
                              <label>Rotate</label>
                              <input
                                type="range"
                                min="-360"
                                max="360"
                                value={imageEditor.rotate}
                                onChange={(e) => setImageEditor(prev => ({...prev, rotate: parseInt(e.target.value)}))}
                              />
                              <span>{imageEditor.rotate}°</span>
                            </div>
                            
                            <div className="control-group">
                              <label>Position X</label>
                              <input
                                type="range"
                                min="-200"
                                max="200"
                                value={imageEditor.x}
                                onChange={(e) => setImageEditor(prev => ({...prev, x: parseInt(e.target.value)}))}
                              />
                              <span>{imageEditor.x}px</span>
                            </div>
                            
                            <div className="control-group">
                              <label>Position Y</label>
                              <input
                                type="range"
                                min="-200"
                                max="200"
                                value={imageEditor.y}
                                onChange={(e) => setImageEditor(prev => ({...prev, y: parseInt(e.target.value)}))}
                              />
                              <span>{imageEditor.y}px</span>
                            </div>
                            
                            <div className="control-group">
                              <label>Ring Thickness</label>
                              <input
                                type="range"
                                min="2"
                                max="50"
                                value={imageEditor.borderWidth}
                                onChange={(e) => setImageEditor(prev => ({...prev, borderWidth: parseInt(e.target.value)}))}
                              />
                              <span>{imageEditor.borderWidth}px</span>
                            </div>
                          </div>
                        </div>
                        
                        <div className="image-editor-actions">
                          <button 
                            className="editor-reset-button"
                            onClick={() => setImageEditor(prev => ({
                              ...prev,
                              scale: 1,
                              rotate: 0,
                              x: 0,
                              y: 0,
                              borderWidth: 8
                            }))}
                          >
                            🔄 Reset
                          </button>
                          <button 
                            className="editor-cancel-button"
                            onClick={() => setShowImageEditor(false)}
                          >
                            Cancel
                          </button>
                          <button 
                            className="editor-apply-button"
                            onClick={applyImageEdits}
                          >
                            Apply Changes
                          </button>
                        </div>
                      </div>
                    </div>
                  )}

                  {/* Ring Preview */}
                  {(newRing.name || imagePreview || newRing.imageUrl) && !showImageEditor && (
                    <div className="ring-creation-preview">
                      <h4>Ring Preview</h4>
                      <div className="preview-display">
                        <ProfileRing 
                          ringId={null}
                          size="large"
                          ringData={{
                            id: 'preview',
                            name: newRing.name || 'Custom Ring',
                            imageUrl: imagePreview || newRing.imageUrl,
                            cssClass: newRing.cssClass
                          }}
                        >
                          <div className="demo-avatar">
                            <span>👤</span>
                          </div>
                        </ProfileRing>
                        <div className="preview-info">
                          <div className="preview-name">{newRing.name || 'Untitled Ring'}</div>
                          <div className="preview-description">{newRing.description || 'No description'}</div>
                          {(imagePreview || newRing.imageUrl) && (
                            <div className="preview-image-status">✓ Custom Image</div>
                          )}
                          {selectedFile && (
                            <button 
                              type="button"
                              className="edit-image-button"
                              onClick={() => setShowImageEditor(true)}
                            >
                              ✏️ Edit Image
                            </button>
                          )}
                        </div>
                      </div>
                    </div>
                  )}
                  
                  <button 
                    type="submit" 
                    className="create-ring-button"
                    disabled={creating || uploadingImage}
                  >
                    {uploadingImage ? 'Uploading Image...' : creating ? 'Creating Ring...' : 'Create Ring'}
                  </button>
                </form>
                
                {/* Created Custom Rings */}
                {customRings.length > 0 && (
                  <div className="custom-rings-list">
                    <h4>Created Custom Rings</h4>
                    <div className="custom-rings-grid">
                      {customRings.map(ring => (
                        <div key={ring.id} className="custom-ring-item">
                          <div className="custom-ring-info">
                            <div className="custom-ring-name">{ring.name}</div>
                            <div className="custom-ring-description">{ring.description}</div>
                            <div className="custom-ring-meta">
                              <span>CSS: {ring.cssClass}</span>
                              {ring.imageUrl && <span>Has Image</span>}
                            </div>
                          </div>
                        </div>
                      ))}
                    </div>
                  </div>
                )}
              </div>
            )}
          </>
        )}
      </div>
    </div>
  );
}

export default AdminRingManager;